import {
  createStackableChart,
  createContext,
  createSlots,
  useStableEventHandlers,
} from '../../core/_summaryze-chart';
import * as Options from './historical-chart-options';
import * as React from 'react';
import Highcharts from 'highcharts';
import Highstock from 'highcharts/highstock';

import type {
  ChartInstanceRef,
  StackableSeriesProps,
  StackableInstanceWrapperProps,
} from '../../core/_summaryze-chart';
import type {
  HistoricalChartEventHandlers,
  HistoricalChartOptions,
  HistoricalChartSeriesOptions,
} from './historical-chart-types';
import { useGlobalization } from '../../../i18n';
import styled from 'styled-components';
import { Dropdown } from '../../core/dropdown';
import { resolutionToZoomButtons } from '../../presets/insight-historical-chart/insight-historical-chart.utils';

import HC_brokenAxis from 'highcharts/modules/broken-axis';
import {
  SharedStoreState,
  useIsFeatureEnabled,
  useSettings,
} from '@innovyze/stylovyze';
import { IconButton, Menu, MenuItem, Switch, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { DateTime, Duration } from 'luxon';
import { getTheme, getThemeColor } from '../../core/utils/theme-utils';
import { noDataLegendProps } from '../../utils/renderer';
import { useSelector } from 'react-redux';
import { TimeSeriesData } from '../../../index';
import { formatWithSignificantFractionDigits } from '../../utils/format-with-significant-fraction-digits';
import { useControllableState } from '../../utils/use-controllable-state';

HC_brokenAxis(Highcharts);

type HighchartsRuntimeSeries = Highcharts.Series & {
  legendGroup?: { element?: SVGGElement };
  navigatorSeries?: Highcharts.Series;
};

const NO_HANDLEBARS_OPTIONS: Highcharts.Options = {
  navigator: { handles: { enabled: false, height: 0 } },
  scrollbar: { enabled: false, height: 0 },
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Utilities
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const DAILY_DATA_GAP_SIZE = Duration.fromObject({ days: 1 }).toMillis();

function getSeriesDataLabelsEnabled(
  series: Highcharts.Series | undefined
): boolean {
  // Highcharts may normalize `dataLabels` to either an object or an array of
  // objects, so handle both shapes when reading the current state.
  const dataLabels = (
    series?.options as {
      dataLabels?: { enabled?: boolean } | { enabled?: boolean }[];
    }
  )?.dataLabels;
  if (Array.isArray(dataLabels)) return !!dataLabels[0]?.enabled;
  return !!dataLabels?.enabled;
}

function getSeriesMarkersEnabled(
  series: Highcharts.Series | undefined
): boolean {
  return !!(series?.options as { marker?: { enabled?: boolean } })?.marker
    ?.enabled;
}

// Returns the timestamp of the latest data point that has a non-null value, so
// "Zoom to series" lands on real data instead of a trailing run of nulls (which
// would zoom to a blank region). `xData`/`yData` are internal parallel arrays
// not present in the type definitions.
function getLatestNonNullTimestamp(
  series: Highcharts.Series | undefined
): number | undefined {
  const runtimeSeries = series as
    | (Highcharts.Series & {
        xData?: number[];
        yData?: (number | null | (number | null)[])[];
      })
    | undefined;

  const xData = runtimeSeries?.xData;
  const yData = runtimeSeries?.yData;
  if (!Array.isArray(xData) || !Array.isArray(yData)) return undefined;

  for (let i = yData.length - 1; i >= 0; i--) {
    const y = yData[i];
    const isNull =
      y === null ||
      y === undefined ||
      (Array.isArray(y) && y.every((v) => v === null || v === undefined));
    if (!isNull) return xData[i];
  }

  return undefined;
}

function fixMinMax(value: string | number | undefined): number | null {
  const n = Number(value);
  if (typeof value === 'undefined') return null;
  else if (typeof value === 'string' && value !== '') return null;
  else if (typeof value === 'string' && !Number.isNaN(n)) return n;
  else if (typeof value === 'number' && !Number.isNaN(value)) return value;
  return null;
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Chart
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const Chart = createStackableChart('HistoricalChart', undefined, true);

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Slots
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const Slots = createSlots<'series-group'>('HistoricalChart');

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Root Component
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const LockedTag = styled.button`
  border-radius: 2px;
  border: none;
  color: #4a6067;
  font-family: 'Lucida Grande', sans-serif;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 6px;
  height: 25px;
  width: 84px;
`;

type HistoricalChartRootProps = HistoricalChartOptions &
  HistoricalChartEventHandlers & {
    children: React.ReactNode;
  };

const [RootContextProvider, useRootContext] = createContext<
  HistoricalChartOptions &
    Pick<HistoricalChartEventHandlers, 'onXAxisExtremesChange'>
>('RootContextProvider');

const HistoricalChartRoot = React.forwardRef<
  ChartInstanceRef,
  HistoricalChartRootProps
>((props, ref) => {
  return (
    <Chart.ChartRoot>
      <_HistoricalChartRoot {...props} ref={ref} />
    </Chart.ChartRoot>
  );
});

HistoricalChartRoot.displayName = 'HistoricalChartRoot';

const _HistoricalChartRoot = React.forwardRef<
  ChartInstanceRef,
  HistoricalChartRootProps
>((props, ref): React.ReactElement => {
  const { t } = useGlobalization();
  const { instanceMapRef } = Chart.useRootContext('_HistoricalChartRoot');
  const areZoomButtonsEnabled = useIsFeatureEnabled(
    'info-360-analytics-zoom-buttons'
  );

  const noHandlebars = useIsFeatureEnabled('info-360-analytics-no-handlebars');

  // Legend Click

  const [clickedLegendSeries, setClickedLegendSeries] =
    React.useState<HighchartsRuntimeSeries>();

  // Fixed screen position for the legend menu, captured when the legend item is
  // clicked. Anchoring to a position (rather than the legend's SVG element)
  // keeps the menu in place when toggling options redraws the chart and
  // recreates that element (which would otherwise snap the menu to the corner).
  const [legendMenuPosition, setLegendMenuPosition] = React.useState<{
    top: number;
    left: number;
  }>();

  // Chart-level settings menu (anchored to the gear button in the header)

  const [settingsAnchorEl, setSettingsAnchorEl] =
    React.useState<HTMLElement | null>(null);

  const handleSettingsMenuOpen = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setSettingsAnchorEl(event.currentTarget);
    },
    []
  );

  const handleSettingsMenuClose = React.useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  // Chart-level options support both controlled (parent owns the state and is
  // notified via the callback) and uncontrolled (the chart owns the state)
  // usage, via useControllableState.

  const [enableChartStack, setEnableChartStack] = useControllableState({
    prop: props.enableChartStack,
    defaultProp: false,
    onChange: props.onEnableChartStackChange,
  });

  const [enableHorizontalGrids, setEnableHorizontalGrids] =
    useControllableState({
      prop: props.enableHorizontalGrids,
      defaultProp: false,
      onChange: props.onEnableHorizontalGridsChange,
    });

  const [enableVerticalGrids, setEnableVerticalGrids] = useControllableState({
    prop: props.enableVerticalGrids,
    defaultProp: false,
    onChange: props.onEnableVerticalGridsChange,
  });

  // The legend lives on the footer instance, so `clickedLegendSeries` is the
  // navigator's copy of the series. Data labels must be applied to the plot
  // series instead (the overlay/stacked-series instances), mirroring what the
  // "Sets series data labels" useEffect targets. The footer/header instances
  // only host the navigator and shared axis and must be skipped.
  //
  // Series are matched by `id` when available, otherwise by their Highcharts
  // `index` (data series are added first and keep the same order across every
  // instance, so the index is stable even when no id is provided).
  const findPlotSeries = React.useCallback(
    (target: HighchartsRuntimeSeries | undefined): Highcharts.Series[] => {
      if (!target) return [];

      const targetId = target.options.id;
      const plotSeries: Highcharts.Series[] = [];
      instanceMapRef.current.forEach((instance, instanceId) => {
        if (instanceId === 'stacked-footer' || instanceId === 'stacked-header')
          return;
        const series = instance.series?.find((s) =>
          targetId !== undefined
            ? s.options.id === targetId
            : s.index === target.index
        );
        if (series) plotSeries.push(series);
      });

      return plotSeries;
    },
    [instanceMapRef]
  );

  // Reflects whether the clicked series currently shows data labels. The
  // Highcharts series is the source of truth (it stays in sync whether the
  // chart owns the state or the parent controls it via props.enableDataLabels).
  const [showDataValues, setShowDataValues] = React.useState(false);

  React.useEffect(() => {
    const plotSeries = findPlotSeries(clickedLegendSeries);
    setShowDataValues(getSeriesDataLabelsEnabled(plotSeries[0]));
  }, [clickedLegendSeries, findPlotSeries]);

  const handleLegendMenuClose = React.useCallback(() => {
    setClickedLegendSeries(undefined);
  }, []);

  const { onSeriesDataLabelsChange } = props;

  const handleToggleSeriesDataLabels = React.useCallback(() => {
    const plotSeries = findPlotSeries(clickedLegendSeries);
    if (plotSeries.length === 0) return;

    const enabled = !getSeriesDataLabelsEnabled(plotSeries[0]);
    setShowDataValues(enabled);

    // Uncontrolled: update the plot series directly so the toggle works even
    // when the parent doesn't control `enableDataLabels`.
    plotSeries.forEach((series) => {
      try {
        series.update({
          dataLabels: { enabled },
        } as Highcharts.SeriesOptionsType);
      } catch (error) {
        console.warn(error);
      }
    });

    // Controlled: notify the parent so it can persist the change and pass it
    // back via props.enableDataLabels, which the series useEffect re-applies.
    const seriesId = clickedLegendSeries?.options.id;
    if (seriesId) onSeriesDataLabelsChange?.(seriesId, enabled);
  }, [clickedLegendSeries, findPlotSeries, onSeriesDataLabelsChange]);

  // Reflects whether the clicked series currently shows data point markers.
  // Same controlled/uncontrolled mechanism as the data labels toggle above.
  const [showDataPoints, setShowDataPoints] = React.useState(false);

  React.useEffect(() => {
    const plotSeries = findPlotSeries(clickedLegendSeries);
    setShowDataPoints(getSeriesMarkersEnabled(plotSeries[0]));
  }, [clickedLegendSeries, findPlotSeries]);

  const { onSeriesMarkersChange } = props;

  const handleToggleSeriesMarkers = React.useCallback(() => {
    const plotSeries = findPlotSeries(clickedLegendSeries);
    if (plotSeries.length === 0) return;

    const enabled = !getSeriesMarkersEnabled(plotSeries[0]);
    setShowDataPoints(enabled);

    // Uncontrolled: update the plot series directly so the toggle works even
    // when the parent doesn't control `enableMarkers`.
    plotSeries.forEach((series) => {
      try {
        series.update({
          marker: { enabled, radius: enabled ? 4 : 0 },
        } as Highcharts.SeriesOptionsType);
      } catch (error) {
        console.warn(error);
      }
    });

    // Controlled: notify the parent so it can persist the change and pass it
    // back via props.enableMarkers, which the series useEffect re-applies.
    const seriesId = clickedLegendSeries?.options.id;
    if (seriesId) onSeriesMarkersChange?.(seriesId, enabled);
  }, [clickedLegendSeries, findPlotSeries, onSeriesMarkersChange]);

  const handleLegendSeriesVisibilityChange = React.useCallback(() => {
    if (!clickedLegendSeries) return;
    setClickedLegendSeries(undefined);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    try {
      if (clickedLegendSeries.options.visible) {
        clickedLegendSeries.hide();
      } else {
        clickedLegendSeries.show();
      }
    } catch (error) {
      console.warn(error);
    }
  }, [clickedLegendSeries]);

  const handleLegendSeriesExtremesChange = React.useCallback(() => {
    // The following code contains props not included in the d.ts files
    // but you can see them by console.log
    if (!clickedLegendSeries) return;

    const navigatorSeries = clickedLegendSeries.navigatorSeries;
    const xAxis = Array.isArray(clickedLegendSeries.chart.xAxis)
      ? clickedLegendSeries.chart.xAxis[0]
      : clickedLegendSeries.chart.xAxis;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const cot = xAxis?.userMin as number | undefined;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const clt = xAxis?.userMax as number | undefined;
    // Use the latest non-null point so we don't zoom into a trailing run of
    // null values (which would land on a blank region of the chart).
    const nslt = getLatestNonNullTimestamp(navigatorSeries);

    if (
      typeof cot === 'number' &&
      typeof clt === 'number' &&
      typeof nslt === 'number'
    ) {
      const range = clt - cot;
      const from = nslt - range;
      const to = DateTime.fromMillis(nslt)
        .plus({ days: 1 })
        .endOf('day')
        .toMillis();

      setClickedLegendSeries(undefined);
      clickedLegendSeries.xAxis.setExtremes(from, to, true, null, {
        trigger: 'centerToSeries',
      });
    }
  }, [clickedLegendSeries]);

  const onLegendItemClick: Highcharts.SeriesLegendItemClickCallbackFunction =
    React.useCallback((event) => {
      const target = event.target as HighchartsRuntimeSeries;
      setClickedLegendSeries(target);

      // Capture the legend item's screen position now, while its element is
      // still mounted, so the menu stays anchored across chart redraws.
      const element = target.legendGroup?.element;
      if (element) {
        const rect = element.getBoundingClientRect();
        setLegendMenuPosition({ top: rect.top, left: rect.left });
      }
    }, []);

  //

  const eventHandlersRef = useStableEventHandlers<HistoricalChartEventHandlers>(
    {
      onLegendItemClick,
      onPointClick: props.onPointClick,
      onXAxisExtremesChange: props.onXAxisExtremesChange,
      onSeriesVisibilityChange: props.onSeriesVisibilityChange,
      syncDateTimeRangePicker: Chart.useRootContext('HistoricalChartRoot')
        .syncDateTimeRangePicker,
    }
  );

  const stackedFooterInstanceInitialOptions = React.useMemo(() => {
    const options = Highcharts.merge<Highcharts.Options>(
      false,
      Options.stackedFooterInstanceInitialOptions,
      noHandlebars ? NO_HANDLEBARS_OPTIONS : {}
    );

    return Options.makeOptionsWithEventHandlers(options, eventHandlersRef);
  }, [eventHandlersRef, noHandlebars]);

  const overlayMainInstanceInitialOptions = React.useMemo(() => {
    const options = Highcharts.merge<Highcharts.Options>(
      false,
      Options.overlayInstanceInitialOptions,
      noHandlebars ? NO_HANDLEBARS_OPTIONS : {}
    );

    return Options.makeOptionsWithEventHandlers(options, eventHandlersRef);
  }, [eventHandlersRef, noHandlebars]);

  const overlayFooterInstanceInitialOptions = React.useMemo(() => {
    const options = Highcharts.merge<Highcharts.Options>(
      false,
      Options.stackedFooterInstanceInitialOptions,
      noHandlebars ? NO_HANDLEBARS_OPTIONS : {}
    );

    return Options.makeOptionsWithEventHandlers(options, eventHandlersRef);
  }, [eventHandlersRef, noHandlebars]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const instance =
    instanceMapRef.current.get('overlay-instance') ||
    instanceMapRef.current.get('stacked-footer');

  const axis = instance?.xAxis?.at(0);
  const extremes = axis?.getExtremes() ?? ({} as Highcharts.ExtremesObject);

  let selectedZoomOption =
    extremes?.max && extremes?.min ? extremes.max - extremes.min : '';

  if (props.zoomOptions) {
    if (!props.zoomOptions.some((o) => o.value === selectedZoomOption)) {
      selectedZoomOption = '';
    }
  }

  const disableMinusZoomButton = React.useMemo(() => {
    if (props.zoomMaxTimeRange === undefined) return false;
    if (extremes?.min === undefined || extremes?.max === undefined) return;

    const currentRange = extremes.max - extremes.min;
    const result = currentRange >= props.zoomMaxTimeRange;

    return result;
  }, [extremes?.min, extremes?.max, props.zoomMaxTimeRange]);

  const disblePlusZoomButton = React.useMemo(() => {
    if (props.zoomMinTimeRange === undefined) return false;
    if (extremes?.min === undefined || extremes?.max === undefined) return;

    const currentRange = extremes.max - extremes.min;
    const result = currentRange <= props.zoomMinTimeRange;

    return result;
  }, [extremes?.min, extremes?.max, props.zoomMinTimeRange]);

  const handleZoomChange = (change: 'decrease' | 'increase') => {
    if (extremes?.min === undefined || extremes?.max === undefined) return;

    const diff = extremes.max - extremes.min;
    const delta = Math.trunc(diff * 0.2);

    let nextMin =
      change === 'decrease' ? extremes.min - delta : extremes.min + delta;
    let nextMax =
      change === 'decrease' ? extremes.max + delta : extremes.max - delta;

    if (nextMin < extremes.dataMin) nextMin = extremes.dataMin;
    if (nextMax > extremes.dataMax) nextMax = extremes.dataMax;

    axis.setExtremes(nextMin, nextMax, undefined, undefined, {
      trigger: 'zoom',
    });
  };

  const handleZoomDropdownChange = (value: string | number) => {
    if (extremes?.max === undefined) return;
    const delta = Number(value);
    const min = extremes.max - delta;
    axis.setExtremes(min, extremes.max, undefined, undefined, {
      trigger: 'zoom',
    });
  };

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  return (
    <>
      <Header>
        <Chart.ChartTimeRange />
        {areZoomButtonsEnabled ? (
          <ZoomButtonsContainer>
            <ZoomButton
              data-cy="time-range-zoom-out-button"
              onClick={() => handleZoomChange('decrease')}
              disabled={disableMinusZoomButton}>
              -
            </ZoomButton>
            <ZoomButton
              data-cy="time-range-zoom-in-button"
              onClick={() => handleZoomChange('increase')}
              disabled={disblePlusZoomButton}>
              +
            </ZoomButton>
          </ZoomButtonsContainer>
        ) : null}
        {props.zoomOptions ? (
          <Dropdown
            label="Zoom"
            data-cy="time-range-zoom-selector"
            options={props.zoomOptions}
            selected={selectedZoomOption}
            onChange={handleZoomDropdownChange}
          />
        ) : null}
        {props?.isForecast ? (
          <Tooltip title={t('Resolution locked to forecast')} placement="top">
            <LockedTag>{t('15 Minutes')}</LockedTag>
          </Tooltip>
        ) : props.resolutionOptions ? (
          <Dropdown {...props.resolutionOptions} />
        ) : null}
        {props.showLiveDataButton ? (
          <LiveToggleButtonContainer data-cy="liveButton">
            <LiveToggleButton
              isLiveData={props.isLiveData}
              onClick={() => props.onLiveDataToggle?.(!props.isLiveData)}>
              {t('Live')}
            </LiveToggleButton>
          </LiveToggleButtonContainer>
        ) : null}
        <Tooltip title={t('Chart settings')} placement="top">
          <IconButton
            data-cy="chart-settings-button"
            aria-label={t('Chart settings')}
            size="small"
            onClick={handleSettingsMenuOpen}
            sx={{
              color: '#4a6067',
              backgroundColor: '#e6ebf5',
              borderRadius: '4px',
              height: 25,
              width: 25,
              '&:hover': { backgroundColor: '#d8dee9' },
            }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={settingsAnchorEl}
          open={!!settingsAnchorEl}
          onClose={handleSettingsMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem
            dense
            data-cy="chart-settings-mode"
            onClick={() => {
              setEnableChartStack(!enableChartStack);
              handleSettingsMenuClose();
            }}>
            {enableChartStack ? t('Overlay') : t('Stack')}
          </MenuItem>
          <MenuItem
            dense
            data-cy="chart-settings-horizontal-grid"
            sx={{ justifyContent: 'space-between', gap: 2 }}
            onClick={() => setEnableHorizontalGrids(!enableHorizontalGrids)}>
            {t('Show horizontal grid')}
            <Switch
              size="small"
              edge="end"
              checked={!!enableHorizontalGrids}
              readOnly
              tabIndex={-1}
            />
          </MenuItem>
          <MenuItem
            dense
            data-cy="chart-settings-vertical-grid"
            sx={{ justifyContent: 'space-between', gap: 2 }}
            onClick={() => setEnableVerticalGrids(!enableVerticalGrids)}>
            {t('Show vertical grid')}
            <Switch
              size="small"
              edge="end"
              checked={!!enableVerticalGrids}
              readOnly
              tabIndex={-1}
            />
          </MenuItem>
          <MenuItem
            dense
            data-cy="chart-settings-data-gap"
            sx={{ justifyContent: 'space-between', gap: 2 }}
            onClick={() => props.onDataGapChange?.(!props.dataGap)}>
            {t('Show time gaps')}
            <Switch
              size="small"
              edge="end"
              checked={!!props.dataGap}
              readOnly
              tabIndex={-1}
            />
          </MenuItem>
        </Menu>
      </Header>
      <RootContextProvider
        separateYAxes={props.separateYAxes}
        enableChartStack={enableChartStack}
        enableColumnSeriesStack={props.enableColumnSeriesStack}
        enableDataLabels={props.enableDataLabels}
        enableHorizontalGrids={enableHorizontalGrids}
        enableVerticalGrids={enableVerticalGrids}
        xAxisLabel={props.xAxisLabel}
        visibleDataExtremes={props.visibleDataExtremes}
        yAxisLabel={props.yAxisLabel}
        defaultLimit={props.defaultLimit}
        lowestResolution={props.lowestResolution}
        onXAxisExtremesChange={props.onXAxisExtremesChange}
        timeStampPointer={props.timeStampPointer}
        status={props.status}
        selectedTheme={props.selectedTheme}>
        {enableChartStack ? (
          <Slots.Slottable slottableChildren={props.children}>
            {(slots) => {
              const series = slots.getSlot('series-group');

              return (
                <Chart.ChartStackedInstances
                  instanceWrapperComponent={StackableInstanceWrapper}
                  headerInstanceInitialOptions={
                    Options.stackedHeaderInstanceInitialOptions
                  }
                  seriesInstanceInitialOptions={
                    Options.stackedSeriesInstanceInitialOptions
                  }
                  footerInstanceInitialOptions={
                    stackedFooterInstanceInitialOptions
                  }
                  constructorFunction={Highstock.stockChart}>
                  {series}
                </Chart.ChartStackedInstances>
              );
            }}
          </Slots.Slottable>
        ) : (
          <Chart.ChartOverlayInstance
            ref={ref}
            instanceWrapperComponent={StackableInstanceWrapper}
            mainInstanceInitialOptions={overlayMainInstanceInitialOptions}
            footerInstanceInitialOptions={overlayFooterInstanceInitialOptions}
            constructorFunction={Highstock.stockChart}>
            {props.children}
          </Chart.ChartOverlayInstance>
        )}
        {enableChartStack ? (
          <div className="ref-holder" style={{ display: 'none' }}>
            <Chart.ChartOverlayInstance
              ref={ref}
              instanceWrapperComponent={StackableInstanceWrapper}
              mainInstanceInitialOptions={overlayMainInstanceInitialOptions}
              footerInstanceInitialOptions={overlayFooterInstanceInitialOptions}
              constructorFunction={Highstock.stockChart}>
              {props.children}
            </Chart.ChartOverlayInstance>
          </div>
        ) : null}
        {/*  Legend Click Menu  */}
        <Menu
          open={!!clickedLegendSeries && !!legendMenuPosition}
          anchorReference="anchorPosition"
          anchorPosition={legendMenuPosition}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          sx={{ transform: 'translateY(-16px)' }}
          onClose={handleLegendMenuClose}>
          <MenuItem
            dense
            disabled={!clickedLegendSeries?.options?.visible}
            onClick={handleLegendSeriesExtremesChange}>
            {t('Zoom to series')}
          </MenuItem>
          <MenuItem dense onClick={handleLegendSeriesVisibilityChange}>
            {`${t((clickedLegendSeries?.options?.visible ?? true) ? 'Hide' : 'Show')} series`}
          </MenuItem>
          <MenuItem
            dense
            sx={{ justifyContent: 'space-between', gap: 2 }}
            onClick={handleToggleSeriesDataLabels}>
            {t('Show Data Values')}
            <Switch
              size="small"
              edge="end"
              checked={showDataValues}
              readOnly
              tabIndex={-1}
            />
          </MenuItem>
          <MenuItem
            dense
            sx={{ justifyContent: 'space-between', gap: 2 }}
            onClick={handleToggleSeriesMarkers}>
            {t('Show Data Points')}
            <Switch
              size="small"
              edge="end"
              checked={showDataPoints}
              readOnly
              tabIndex={-1}
            />
          </MenuItem>
        </Menu>
      </RootContextProvider>
    </>
  );
});

_HistoricalChartRoot.displayName = '_HistoricalChartRoot';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;

  margin-bottom: 6px;
  padding: 0 20px;
  width: 100%;
`;

const LiveToggleButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const LiveToggleButton = styled.button<{ isLiveData?: boolean }>`
  background-color: ${(props) => (props.isLiveData ? '#98fb98' : '#e6ebf5')};
  border-radius: 2px;
  border: none;
  color: #4a6067;
  cursor: pointer;
  font-family: 'Lucida Grande', sans-serif;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 6px;
  height: 25px;
`;

const ZoomButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const ZoomButton = styled.button`
  background-color: #e6ebf5;
  border-radius: 2px;
  border: none;
  color: #4a6067;
  cursor: pointer;
  font-family: 'Lucida Grande', sans-serif;
  font-size: 12px;
  font-weight: bolder;
  padding: 4px 6px;
  height: 25px;
  width: 25px;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Series Group Component
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */
interface HistoricalChartSeriesGroupProps {
  children: React.ReactNode;
}

const HistoricalChartSeriesGroup = (
  props: HistoricalChartSeriesGroupProps
): React.ReactElement => {
  return <Slots.Slot id="series-group">{props.children}</Slots.Slot>;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Series Component
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

// Note: To set an ordered zIndex to all items in an array
// where first element is in front and last element is behind
// zIndex = <array.length> - (<index of element> % <array.length>)

type HistoricalChartSeriesProps = HistoricalChartSeriesOptions;

const HistoricalChartSeries = (
  props: HistoricalChartSeriesProps & StackableSeriesProps
): null => {
  const { t } = useGlobalization();
  const separateYAxisRef = React.useRef<Highcharts.Axis>();
  const seriesRef = Chart.useSeries(props, 'HistoricalChartSeries');
  const instanceRef = Chart.useInstance('HistoricalChartSeries');
  const instanceId = Chart.useInstanceId('HistoricalChartSeries');
  const rootContext = useRootContext('HistoricalChartSeries');

  // The footer/header instances host the navigator and shared axis. Their
  // copies of the series must never show data labels or markers — and in
  // overlay mode they can't be told apart by `stackedInstanceType` (it stays
  // undefined), so we key off the instance id instead.
  const isNavigatorInstance =
    instanceId === 'stacked-footer' || instanceId === 'stacked-header';
  const trendlineSeriesRef = React.useRef<Highcharts.Series>();
  const panningSeriesRef = React.useRef<Highcharts.Series>();
  const legendSeriesRef = React.useRef<Highcharts.SVGElement>();

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series type
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    let options = {} as Highcharts.SeriesOptionsType;

    if (props.type === 'scatter') {
      options = {
        type: 'line',
        lineWidth: 0,
        marker: { enabled: true, radius: 4 },
        states: { hover: { lineWidthPlus: 0 } },
      } as Highcharts.SeriesOptionsType;
    } else {
      options = {
        type: props.type,
        lineWidth: 2,
        marker: {
          enabled: props.enableMarkers,
          radius: props.enableMarkers ? 4 : 0,
        },
        states: { hover: { lineWidthPlus: 1 } },
      } as Highcharts.SeriesOptionsType;
    }

    const _analytic =
      typeof props.analytic === 'string'
        ? props.analytic
        : props.analytic?.type;

    if (_analytic === 'Constant' && props.type === 'line')
      options = {
        ...options,
        connectNulls: true,
      } as Highcharts.SeriesOptionsType;

    seriesRef.current?.update(options);
  }, [
    seriesRef,
    props.type,
    props.analytic,
    props.stackedInstanceType,
    props.enableMarkers,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series unit
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    seriesRef.current?.update({
      custom: { unit: props.unit },
    } as unknown as Highcharts.SeriesOptionsType);
  }, [seriesRef, props.unit]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series name
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    seriesRef.current?.update({
      name: props.name,
    } as Highcharts.SeriesOptionsType);
  }, [seriesRef, props.name]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series zIndex
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    seriesRef.current?.update({
      zIndex: props.zIndex,
    } as Highcharts.SeriesOptionsType);
  }, [seriesRef, props.zIndex]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series visibility
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    seriesRef.current?.update({
      visible: !props.hidden,
    } as Highcharts.SeriesOptionsType);
  }, [seriesRef, props.hidden]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series data
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (props.stackedInstanceType === undefined) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      seriesRef.current?.update({
        data: props.data,
        gapSize: props.dataGapSize,
        navigatorOptions: {
          data: props.navigatorData,
          gapSize: DAILY_DATA_GAP_SIZE,
        },
      } as Highcharts.SeriesOptionsType);
    } else if (props.stackedInstanceType === 'stacked-series') {
      seriesRef.current?.update({
        data: props.data,
        gapSize: props.dataGapSize,
      } as Highcharts.SeriesOptionsType);
    } else {
      seriesRef.current?.update({
        data: props.navigatorData,
        gapSize: DAILY_DATA_GAP_SIZE,
      } as Highcharts.SeriesOptionsType);
    }
  }, [
    seriesRef,
    props.data,
    props.dataGapSize,
    props.navigatorData,
    props.stackedInstanceType,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Adds navigator data to series
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (props.stackedInstanceType === undefined) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      seriesRef.current?.update({
        navigatorOptions: {
          data: props.navigatorData,
          gapSize: DAILY_DATA_GAP_SIZE,
        },
      } as Highcharts.SeriesOptionsType);
    }
  }, [seriesRef, props.navigatorData, props.stackedInstanceType]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series data labels
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined ||
      props.stackedInstanceType === 'stacked-series'
    ) {
      seriesRef.current?.update({
        dataLabels: {
          enabled: props.enableDataLabels ?? rootContext.enableDataLabels,
        },
      } as Highcharts.SeriesOptionsType);
    }
  }, [
    seriesRef,
    props.enableDataLabels,
    props.stackedInstanceType,
    rootContext.enableDataLabels,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets data markers
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined ||
      props.stackedInstanceType === 'stacked-series'
    ) {
      if (props.type !== 'scatter') {
        seriesRef.current?.update({
          marker: {
            enabled: props.enableMarkers,
            radius: props.enableMarkers ? 4 : 0,
          },
        } as Highcharts.SeriesOptionsType);
      }
    }
  }, [seriesRef, props.enableMarkers, props.stackedInstanceType, props.type]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Keeps the navigator instance free of data labels and markers
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  // Runs after the data labels / markers / type effects so it always wins for
  // the navigator (footer) and header instances, overriding any enabled state
  // those effects applied (in overlay mode they can't guard by instance type).
  React.useEffect(() => {
    if (isNavigatorInstance) {
      seriesRef.current?.update({
        dataLabels: { enabled: false },
        marker: { enabled: false, radius: 0 },
      } as Highcharts.SeriesOptionsType);
    }
  }, [
    seriesRef,
    isNavigatorInstance,
    props.enableDataLabels,
    props.enableMarkers,
    props.type,
    props.analytic,
    rootContext.enableDataLabels,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets column series stack
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    let stacking: Highcharts.OptionsStackingValue | undefined = undefined;

    if (props.type === 'column') {
      if (
        props.enableColumnSeriesStack ??
        rootContext.enableColumnSeriesStack
      ) {
        stacking = 'normal';
      }
    }

    seriesRef.current?.update({ stacking } as Highcharts.SeriesOptionsType);
  }, [
    seriesRef,
    rootContext.enableColumnSeriesStack,
    props.enableColumnSeriesStack,
    props.type,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets overlay mode separate Y axis
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const prevSeparateYAxisRef = React.useRef(separateYAxisRef.current);

  React.useEffect(() => {
    // The extra axis only gets added if chart is NOT in stack mode and doesnt
    // exist already.
    if (props.stackedInstanceType === undefined) {
      if (props.separateYAxisId !== undefined) {
        const existingSeparateYAxis = instanceRef.current.yAxis.find(
          (a) => a.options.id === props.separateYAxisId
        );

        if (existingSeparateYAxis) {
          separateYAxisRef.current = existingSeparateYAxis;
        } else {
          separateYAxisRef.current = instanceRef.current?.addAxis({
            id: props.separateYAxisId,
          });
        }
      } else {
        separateYAxisRef.current = undefined;
      }

      seriesRef.current?.update({
        yAxis: props.separateYAxisId || 0,
      } as Highcharts.SeriesOptionsType);

      if (prevSeparateYAxisRef.current?.series.length === 0) {
        try {
          prevSeparateYAxisRef.current?.remove();
          prevSeparateYAxisRef.current = undefined;
        } catch (error) {
          console.log(error);
        }
      } else if (prevSeparateYAxisRef.current) {
        const nextColor = prevSeparateYAxisRef.current.series[0].options.color;
        prevSeparateYAxisRef.current.update({
          title: { style: { color: nextColor.toString() } },
        });
      }

      prevSeparateYAxisRef.current = separateYAxisRef.current;

      return () => {
        if (separateYAxisRef.current?.series.length === 0) {
          try {
            separateYAxisRef.current?.remove();
            separateYAxisRef.current = undefined;
          } catch (error) {
            console.log(error);
          }
        } else if (separateYAxisRef.current) {
          const nextColor = separateYAxisRef.current.series[0].options.color;
          separateYAxisRef.current.update({
            title: { style: { color: nextColor.toString() } },
          });
        }
      };
    }
  }, [
    seriesRef,
    instanceRef,
    props.separateYAxisId,
    props.stackedInstanceType,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series color
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    let options = {
      color: props.color,
      lineColor: props.color,
    } as Highcharts.SeriesOptionsType;

    if (options.color === undefined) {
      if (props.stackedSeriesIndex !== undefined) {
        const theme = getTheme(rootContext.selectedTheme);
        const defaultColors = theme.colors;
        const wrappedIndex = props.stackedSeriesIndex % theme.colors.length;
        options.color = defaultColors?.[wrappedIndex];
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        options.lineColor = defaultColors?.[wrappedIndex];
      } else {
        // Color does exist in series after it's created
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const newColor = getThemeColor(
          rootContext.selectedTheme,
          seriesRef.current?.options?.index
        );

        options.color = newColor;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        options.lineColor = newColor;
      }
    }

    if (props.type === 'candlestick') {
      options = {
        ...options,
        lineColor: options.color,
      } as Highcharts.SeriesCandlestickOptions;
    }

    seriesRef.current?.update(options);

    if (props.stackedSeriesIndex === undefined && separateYAxisRef.current) {
      separateYAxisRef.current.update({
        title: { style: { color: options.color as string } },
      });
    }
  }, [
    instanceRef,
    seriesRef,
    props.type,
    props.color,
    props.stackedSeriesIndex,
    separateYAxisRef.current,
    rootContext.selectedTheme,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets series dash style
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    seriesRef.current?.update({
      dashStyle: props.dashStyle,
    } as Highcharts.SeriesOptionsType);
  }, [seriesRef, props.dashStyle]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets trendline series
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (props.enableTrendline) {
      const trendlineSeriesId = props.id + '_trendline';
      trendlineSeriesRef.current = instanceRef.current?.addSeries({
        type: 'line',
        id: trendlineSeriesId,
        showInNavigator: false,
      });

      return () => {
        try {
          trendlineSeriesRef.current?.remove();
          trendlineSeriesRef.current = undefined;
        } catch (error) {
          console.log(error);
        }
      };
    }
  }, [instanceRef, trendlineSeriesRef, props.id, props.enableTrendline]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets trendline data
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    trendlineSeriesRef.current?.update({
      data: props.trendlineData,
    } as Highcharts.SeriesOptionsType);
  }, [trendlineSeriesRef.current, props.trendlineData]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets trendline series name
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    trendlineSeriesRef.current?.update({
      name: props.name + ` - ${t('Trendline')}`,
    } as Highcharts.SeriesOptionsType);
  }, [trendlineSeriesRef.current, props.name]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets trendline series color
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    let color = props.trendlineColor;

    if (!color) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      color = seriesRef.current?.color;
    }

    trendlineSeriesRef.current?.update({
      color,
    } as Highcharts.SeriesOptionsType);
  }, [trendlineSeriesRef.current, seriesRef, props.trendlineColor]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets trendline series dash style
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    trendlineSeriesRef.current?.update({
      dashStyle: props.trendlineDashStyle || 'Dash',
    } as Highcharts.SeriesOptionsType);
  }, [trendlineSeriesRef.current, props.trendlineDashStyle]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets trendline series visibility
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    trendlineSeriesRef.current?.update({
      visible: !props.hiddenTrendlineSeries,
    } as Highcharts.SeriesOptionsType);
  }, [trendlineSeriesRef, props.hiddenTrendlineSeries]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set stacked mode series status
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (props.stackedInstanceType === 'stacked-series') {
      if (legendSeriesRef.current?.element) legendSeriesRef.current?.destroy();
      if (rootContext.status === 'loading') {
        instanceRef.current?.showLoading();
      } else if (rootContext.status === 'rejected') {
        instanceRef.current?.showLoading(t('Failed to retrieve data'));
      } else {
        instanceRef.current?.hideLoading();
        const dataExist =
          seriesRef.current.data && seriesRef.current.data.length > 0;
        const { x, y, style } = noDataLegendProps(
          instanceRef.current,
          !dataExist
        );
        legendSeriesRef.current = instanceRef.current?.renderer
          .text('No Data', x, y)
          .css(style)
          .add();
      }
    }
  }, [
    instanceRef,
    seriesRef,
    legendSeriesRef,
    t,
    props.stackedInstanceType,
    rootContext.status,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set stacked mode y axis
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    let unit = props.unit;
    if (unit) unit = `(${unit})`;

    const separateYAxis = rootContext.separateYAxes?.find(
      (a) => a.id === props.separateYAxisId
    );

    if (props.stackedInstanceType === 'stacked-series' && separateYAxis) {
      instanceRef.current.update({
        yAxis: {
          title: { text: separateYAxis.label || unit },
          min: fixMinMax(separateYAxis.min),
          max: fixMinMax(separateYAxis.max),
        },
      });
    } else if (
      props.stackedInstanceType === 'stacked-series' &&
      !separateYAxis
    ) {
      instanceRef.current.update({ yAxis: { title: { text: unit } } });
    }
  }, [
    instanceRef,
    props.separateYAxisId,
    props.stackedInstanceType,
    props.unit,
    rootContext.separateYAxes,
    t,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set Now pointer if is Live
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    const plotLines: Highcharts.XAxisPlotLinesOptions[] = [];
    const colors = getTheme(rootContext.selectedTheme ?? 'Default');
    const color = colors.colors[0];
    for (const timePointer of rootContext.timeStampPointer ?? []) {
      plotLines.push({
        color: color,
        width: 1.5,
        value: timePointer,
        label: {
          text: 'now',
          verticalAlign: 'bottom',
          rotation: 0,
          textAlign: 'center',
          style: {
            color: color,
          },
          y: 0,
        },
      });
    }
    instanceRef.current?.update({
      xAxis: {
        plotLines,
      },
    });
  }, [instanceRef, rootContext.timeStampPointer, rootContext.selectedTheme]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Create hidden series for panning
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (props.index === 0) {
      const panningSeriesId = props.id + '_panning';
      panningSeriesRef.current = instanceRef.current?.addSeries({
        type: 'line',
        id: panningSeriesId,
        visible: true,
        showInLegend: false,
        showInNavigator: true,
        enableMouseTracking: false,
        color: 'transparent',
      });

      return () => {
        try {
          panningSeriesRef.current?.remove();
          panningSeriesRef.current = undefined;
        } catch (error) {
          console.log(error);
        }
      };
    }
  }, [instanceRef, panningSeriesRef, props.id, props.index]);

  React.useEffect(() => {
    const data = [];

    if (props.navigatorDataExtremes?.from && props.navigatorDataExtremes?.to) {
      data.push([props.navigatorDataExtremes.from, 0]);
      data.push([props.navigatorDataExtremes.to, 0]);
    }

    panningSeriesRef.current?.update({
      type: 'scatter',
      data,
    } as Highcharts.SeriesOptionsType);
  }, [
    panningSeriesRef.current,
    props.navigatorDataExtremes?.from,
    props.navigatorDataExtremes?.to,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  return null;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Stackable Instance Wrapper Component
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const StackableInstanceWrapper = (
  props: StackableInstanceWrapperProps
): React.ReactElement => {
  const { t } = useGlobalization();
  const rootContext = useRootContext('StackableInstanceWrapper');
  const instanceRef = Chart.useInstance('StackableInstanceWrapper');
  const instanceSeriesProps =
    Chart.useInstanceSeriesProps<HistoricalChartSeriesProps>(
      'StackableInstanceWrapper'
    );
  const legendSeriesRef = React.useRef<Highcharts.SVGElement>();

  const panningEnabled = useIsFeatureEnabled(
    'info-360-analytics-chart-panning'
  );

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set panning options
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (!panningEnabled) {
      instanceRef.current?.update(
        {
          chart: {
            height: null,
            zoomType: 'x',
          },
        },
        false
      );
    } else {
      instanceRef.current?.update(
        {
          chart: {
            height: null,
            zoomType: undefined,
            panning: {
              enabled: true,
              type: 'x',
            },
          },
        },
        false
      );
    }
  }, [instanceRef, panningEnabled]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set significant digits
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const { companySettings } = useSettings();
  const significantDigitsRef = React.useRef(companySettings.significantDigits);
  significantDigitsRef.current = companySettings.significantDigits;

  React.useEffect(() => {
    instanceRef.current?.update({
      plotOptions: {
        series: {
          dataLabels: {
            // prettier-ignore
            formatter: function () {
              return formatWithSignificantFractionDigits(this.y, significantDigitsRef.current);
            },
          },
        },
      },
      tooltip: {
        useHTML: true,
        // prettier-ignore
        pointFormatter: function () {
          if (this.series.type === 'candlestick') {
            let name = this.series.name;
            const unit = this.series.options.custom?.unit;
            if (unit) name += ` (${unit})`;

            const open = formatWithSignificantFractionDigits(this.options.open, significantDigitsRef.current);
            const high = formatWithSignificantFractionDigits(this.options.high, significantDigitsRef.current);
            const low = formatWithSignificantFractionDigits(this.options.low, significantDigitsRef.current);
            const close = formatWithSignificantFractionDigits(this.options.close, significantDigitsRef.current);

            const content = [
              `<div><b>${name}</b></div>`,
              '<table>',
              `<tr><td>Open</td><td><b style="margin-left: 6px;">${open}</b></td></tr>`,
              `<tr><td>High</td><td><b style="margin-left: 6px;">${high}</b></td></tr>`,
              `<tr><td>Low</td><td><b style="margin-left: 6px;">${low}</b></td></tr>`,
              `<tr><td>Close</td><td><b style="margin-left: 6px;">${close}</b></td></tr>`,
              '</table>'
            ].join('\n');

            return `<div>${content}</div>`;
          } else {
            const content = [];

            const color = this.color;
            const name = this.series.name;
            const unit = this.series.options.custom?.unit;
            let value = formatWithSignificantFractionDigits(this.y, significantDigitsRef.current);
            if (unit) value += ` (${unit})`;

            content.push([
              '<span>',
              `<span style="color: ${color}; font-size: 1.15em;">● </span>`,
              `<span>${name}: </span>`,
              `<b>${value}</b>`, 
              '</span>'
            ].join(''));

            return content.join('');
          }
        },
      },
    });
  }, [instanceRef]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set overlay mode y axes
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined &&
      rootContext.separateYAxes !== undefined
    ) {
      instanceRef.current?.update(
        {
          yAxis: rootContext.separateYAxes.map((a) => ({
            id: a.id,
            title: { text: a.label },
            opposite: a.side === 'right',
            min: fixMinMax(a.min),
            max: fixMinMax(a.max),
          })),
        },
        false
      );
    }
  }, [instanceRef, props.stackedInstanceType, rootContext.separateYAxes]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets Zoom Options
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined ||
      props.stackedInstanceType === 'stacked-header'
    ) {
      const lowestResolution = rootContext.lowestResolution ?? 'RAW';
      const defaultLimit = rootContext.defaultLimit ?? 180;
      const zoomButtons = resolutionToZoomButtons(
        lowestResolution,
        defaultLimit
      );

      instanceRef.current?.update({
        rangeSelector: { buttons: zoomButtons },
      });
    }
  }, [
    instanceRef,
    props.stackedInstanceType,
    rootContext.lowestResolution,
    rootContext.defaultLimit,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets X axis label overlay / stack mode
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined ||
      props.stackedInstanceType === 'stacked-footer'
    ) {
      const title = rootContext.xAxisLabel ?? '';

      instanceRef.current?.update({
        xAxis: { title: { text: title } },
      });
    }
  }, [instanceRef, props.stackedInstanceType, rootContext.xAxisLabel]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets overlay mode main y axis label
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const mainYAxisUnits = React.useMemo(() => {
    const units: Set<string> = new Set();

    instanceSeriesProps.forEach((seriesProps) => {
      if (seriesProps.unit && !seriesProps.separateYAxisId) {
        units.add(seriesProps.unit);
      }
    });

    return units.size > 0 ? `(${Array.from(units).join(', ')})` : undefined;
  }, [instanceSeriesProps]);

  React.useEffect(() => {
    if (props.stackedInstanceType === undefined) {
      let title = rootContext.yAxisLabel ?? '';

      if (mainYAxisUnits) {
        const spacing = title ? ' ' : '';
        title += `${spacing}${mainYAxisUnits}`;
      }

      instanceRef.current?.update({
        yAxis: { title: { text: title } },
      });
    }
  }, [
    instanceRef,
    props.stackedInstanceType,
    rootContext.yAxisLabel,
    mainYAxisUnits,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets horizontal gridlines
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined ||
      props.stackedInstanceType === 'stacked-series'
    ) {
      instanceRef.current?.yAxis[0]?.update({
        gridLineWidth: rootContext.enableHorizontalGrids ? 1 : 0,
      });
    }
  }, [
    instanceRef,
    props.stackedInstanceType,
    rootContext.enableHorizontalGrids,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets vertical gridlines
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    if (
      props.stackedInstanceType === undefined ||
      props.stackedInstanceType === 'stacked-series'
    ) {
      instanceRef.current?.xAxis[0]?.update({
        gridLineWidth: rootContext.enableVerticalGrids ? 1 : 0,
      });
    }
  }, [instanceRef, props.stackedInstanceType, rootContext.enableVerticalGrids]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Set snap selection
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    const min = rootContext.visibleDataExtremes?.from;
    const max = rootContext.visibleDataExtremes?.to;

    if (min !== undefined || max !== undefined) {
      instanceRef.current?.xAxis[0]?.setExtremes(min, max);
    }
  }, [instanceRef, rootContext.visibleDataExtremes]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Get simulations
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const simulations = useSelector(
    (state: SharedStoreState) => state.infoWaterPro.simulations
  );

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets overlay mode status
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  const noData = React.useMemo(() => {
    const status = rootContext.status;
    const noData =
      (status === undefined ||
        status === 'resolved' ||
        status === 'cancelled') &&
      instanceSeriesProps.every((series) => {
        const { data, simulationObjectId } = series;
        const _noData = data === undefined || data.length === 0;
        const isSimulation = !!simulationObjectId;
        if (isSimulation) {
          const isSimulationDeleted = TimeSeriesData.getIsSimulationDeleted(
            series,
            simulations,
            +series.simulationResultId
          );
          return _noData || isSimulationDeleted;
        }

        return _noData;
      });
    return noData;
  }, [instanceSeriesProps, rootContext.status, simulations]);

  React.useEffect(() => {
    if (props.stackedInstanceType === undefined) {
      if (legendSeriesRef.current?.element) legendSeriesRef.current?.destroy();
      if (rootContext.status === 'loading') {
        instanceRef.current?.showLoading();
      } else if (rootContext.status === 'rejected') {
        instanceRef.current?.showLoading(t('Failed to retrieve data'));
      } else {
        instanceRef.current?.hideLoading();
        const { x, y, style } = noDataLegendProps(instanceRef.current, noData);
        legendSeriesRef.current = instanceRef.current?.renderer
          .text('No Data', x, y)
          .css(style)
          .add();
      }
    }
  }, [
    instanceRef,
    t,
    props.stackedInstanceType,
    rootContext.status,
    noData,
    legendSeriesRef,
  ]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * Sets Theme
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  React.useEffect(() => {
    const theme = rootContext.selectedTheme ?? 'Default';
    const themeOptions = getTheme(theme);
    instanceRef.current?.update({ ...themeOptions });
  }, [instanceRef, rootContext.selectedTheme]);

  /* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
   * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

  return <>{props.children}</>;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export {
  HistoricalChartRoot,
  HistoricalChartSeries,
  HistoricalChartSeriesGroup,
};

export type {
  HistoricalChartRootProps,
  HistoricalChartSeriesProps,
  HistoricalChartSeriesGroupProps,
  ChartInstanceRef,
};
